import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCachedImageUrl } from '@/utils/imageCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLUG_TO_CATEGORY = {
  'indoor-seat-cushions': 'indoor',
  'outdoor-seat-cushions': 'outdoor',
  'sofa-cushions': 'sofa',
  'indoor': 'indoor',
  'outdoor': 'outdoor',
};

const CATEGORY_TO_SLUG = {
  indoor: '/products/indoor-seat-cushions',
  outdoor: '/products/outdoor-seat-cushions',
  sofa: '/products/sofa-cushions',
};

const TAB_ITEMS = [
  { key: 'indoor', label: 'Indoor Cushions', shortLabel: 'Indoor' },
  { key: 'outdoor', label: 'Outdoor Cushions', shortLabel: 'Outdoor' },
  { key: 'sofa', label: 'Sofa Cushions', shortLabel: 'Sofa' },
];

const CATEGORY_LABELS = {
  indoor: 'Indoor Cushions',
  outdoor: 'Outdoor Cushions',
  sofa: 'Sofa Cushions',
};

const CATEGORY_DESCRIPTIONS = {
  indoor: (
    <>
      <p className="mb-3">Cozy cushions for your living spaces, dining areas, and bedrooms — handcrafted in Melbourne to your exact dimensions.</p>
      <p className="mb-3">Choose from bench seats, window seats, chair pads, day beds, and more. Every cushion is built with premium high-density foam wrapped in your choice of upholstery fabric, finished with hand-stitched piping and a hidden zip for easy cleaning.</p>
      <p>Most orders are dispatched within 7–10 business days from our Melbourne workshop, with free shipping across Australia.</p>
    </>
  ),
  outdoor: (
    <>
      <p className="mb-3">Weather-resistant cushions for patios, pools, and outdoor furniture — tested against Australian sun, rain, and salt air.</p>
      <p className="mb-3">All outdoor cushions are made with quick-dry foam and UV-stabilised fabrics from premium suppliers like Warwick, Sunbrella, and Sundour. Mould, mildew, and fade resistant by design.</p>
      <p>Made to order in Melbourne with free Australia-wide delivery.</p>
    </>
  ),
  sofa: (
    <>
      <p className="mb-3">Replacement cushions for sofas, sectionals, and lounges — perfectly sized to bring your favourite couch back to its best.</p>
      <p className="mb-3">Choose your foam density, fabric, and zip placement. Whether you need new seat inserts, back cushions, or a complete refresh, we'll match the original feel of your sofa.</p>
      <p>Handcrafted in Melbourne with free shipping Australia-wide.</p>
    </>
  ),
};

const CATEGORY_LANDING = [
  {
    key: 'indoor',
    title: 'Indoor Cushions',
    blurb: 'Bench seats, window cushions, and chair pads — handcrafted to fit your living room, dining room, or reading nook with premium upholstery fabrics.',
    cta: 'Shop Indoor Cushions',
    image: 'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900',
    href: '/products/indoor-seat-cushions',
  },
  {
    key: 'outdoor',
    title: 'Outdoor Cushions',
    blurb: 'UV-resistant, water-shedding cushions for patios, pool lounges, and alfresco dining — built to handle Australian summers without fading.',
    cta: 'Shop Outdoor Cushions',
    image: 'https://images.pexels.com/photos/2079246/pexels-photo-2079246.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900',
    href: '/products/outdoor-seat-cushions',
  },
  {
    key: 'sofa',
    title: 'Sofa Cushions',
    blurb: 'Replacement seat and back cushions for sofas, sectionals, and lounges — get your favourite couch back to its best with foam and covers made to spec.',
    cta: 'Shop Sofa Cushions',
    image: 'https://images.pexels.com/photos/1571453/pexels-photo-1571453.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900',
    href: '/products/sofa-cushions',
  },
];

const TEST_THUMB_V3 = 'https://customer-assets.emergentagent.com/job_613a98c3-3eae-4e44-a2f7-064499799113/artifacts/r6w2mub2_v3.png';
const TEST_THUMB_V4 = 'https://customer-assets.emergentagent.com/job_613a98c3-3eae-4e44-a2f7-064499799113/artifacts/4j8sk9pe_v4.png';
const TEST_THUMB_V5 = 'https://customer-assets.emergentagent.com/job_613a98c3-3eae-4e44-a2f7-064499799113/artifacts/ti5xwqj0_v5.png';
const TEST_THUMB_V6 = 'https://customer-assets.emergentagent.com/job_613a98c3-3eae-4e44-a2f7-064499799113/artifacts/3qxo7j42_v6.png';

const TEST_THUMB_RECTANGLE = 'https://customer-assets.emergentagent.com/job_613a98c3-3eae-4e44-a2f7-064499799113/artifacts/q8uk9vdb_rectangle-bench-seat-cushion.png';

const ProductsPage = () => {
  const { category } = useParams();
  const activeCategory = category ? SLUG_TO_CATEGORY[category] : null;

  // No category in the URL → render the landing page with 3 category cards.
  if (!activeCategory) return <CategoryLanding />;

  return <CategoryProducts activeCategory={activeCategory} />;
};

/* ─── Landing view (3 category cards) — shown at /products ─── */
const CategoryLanding = () => (
  <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream" data-testid="products-page-landing">
    <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
      <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
        <h1
          className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight"
          data-testid="products-title"
        >
          Shop by category
        </h1>
        <p className="text-base sm:text-lg text-gray-500 max-w-2xl font-body">
          Tell us where the cushion goes and we'll show you the right shapes, fabrics, and foams for the job.
        </p>
      </div>
    </section>

    <section className="py-12 sm:py-20">
      <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8" data-testid="category-grid">
          {CATEGORY_LANDING.map((cat) => (
            <Link
              key={cat.key}
              to={cat.href}
              className="group flex flex-col bg-white rounded-3xl overflow-hidden shadow-card hover:shadow-floating transition-all duration-500 hover:-translate-y-1 no-underline"
              data-testid={`category-card-${cat.key}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#f5f0eb] to-[#e8e0d8]">
                <img
                  src={cat.image}
                  alt={cat.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
              <div className="p-7 sm:p-8 flex flex-col flex-1">
                <h2 className="text-2xl sm:text-[28px] font-display font-semibold text-[#0f4c47] mb-3 leading-tight">
                  {cat.title}
                </h2>
                <p className="text-[15px] text-gray-600 font-body leading-relaxed mb-6 flex-1">
                  {cat.blurb}
                </p>
                <span
                  className="inline-flex items-center justify-center gap-2 self-start bg-teal-600 group-hover:bg-teal-700 text-white font-body font-semibold text-sm px-5 py-2.5 rounded-full transition-colors duration-300"
                  data-testid={`category-cta-${cat.key}`}
                >
                  {cat.cta}
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  </div>
);

/* ─── Category products view — shown at /products/:category ─── */
const CategoryProducts = ({ activeCategory }) => {
  const [products, setProducts] = useState([]);
  const [cachedImages, setCachedImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const url = `${API}/cushion-types?category=${activeCategory}`;
        const response = await axios.get(url);
        setProducts(response.data);
        const imageMap = {};
        await Promise.all(
          response.data.map(async (p) => {
            imageMap[p.id] = await getCachedImageUrl(p.image);
          }),
        );
        setCachedImages(imageMap);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [activeCategory]);

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream" data-testid="products-page">
      {/* Filter Tabs (no "All" tab) */}
      <section className="py-6 sm:mt-6 bg-white border-b border-cream-dark">
        <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="inline-flex bg-cream p-1 rounded-full" data-testid="category-tabs">
            {TAB_ITEMS.map((tab) => {
              const isActive = activeCategory === tab.key;
              return (
                <Link
                  key={tab.key}
                  to={CATEGORY_TO_SLUG[tab.key]}
                  className={`rounded-full px-6 py-2 text-sm font-body font-medium transition-colors ${
                    isActive
                      ? 'bg-teal-500 text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid={`tab-${tab.key}`}
                >
                  <span className="sm:hidden">{tab.shortLabel}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Two-column layout: heading/description (1/3) + product grid (2/3) */}
      <section className="py-10 sm:py-16">
        <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
            {/* Left — heading + description (sticky on desktop so it stays in view as you scroll). */}
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-32">
                <h1
                  className="text-[2rem] font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight leading-[1.15]"
                  data-testid="products-title"
                >
                  {CATEGORY_LABELS[activeCategory]}
                </h1>
                <div className="text-sm text-gray-500 font-body leading-relaxed">
                  {CATEGORY_DESCRIPTIONS[activeCategory]}
                </div>
              </div>
            </aside>

            {/* Right — 2-column product grid on desktop, single column on mobile. */}
            <div className="lg:col-span-2">
              {loading ? (
                <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
                  <Loader2 className="h-7 w-7 animate-spin text-teal-500" />
                </div>
              ) : (
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                  data-testid="products-grid"
                >
                  {products.map((product, idx) => {
                    // TEMP TESTING: Rectangle Seat → custom image, every other thumb → v5.
                    const nameLower = (product.name || '').toLowerCase();
                    const overrideThumb = nameLower.includes('rectangle')
                      ? TEST_THUMB_RECTANGLE
                      : TEST_THUMB_V5;
                    const thumbSrc = overrideThumb || cachedImages[product.id] || product.image;
                    return (
                    <Link
                      key={product.id}
                      to={`/customise/${product.id}`}
                      className="block h-full"
                      data-testid={`product-card-${product.id}`}
                    >
                      <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-gray-300 hover:shadow-floating transition-all duration-500 group flex flex-col h-full">
                        <div className="relative h-[19rem] flex-shrink-0 overflow-hidden bg-gradient-to-br from-[#f5f0eb] to-[#e8e0d8]">
                          <img
                            src={thumbSrc}
                            alt={product.name}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700 p-2"
                          />
                        </div>

                        <div className="p-6 flex flex-col justify-center flex-1">
                          <h3 className="text-lg font-display font-semibold text-[#1A1A1A] mb-2">
                            {product.name}
                          </h3>
                          <p className="text-gray-500 text-sm font-body mb-5 leading-relaxed min-h-[4.5rem]">
                            {product.description}
                          </p>
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-xs text-gray-400 font-body">Starting from</span>
                              <p className="text-xl font-display font-semibold text-teal-600">
                                ${product.base_price.toFixed(0)}
                              </p>
                            </div>
                            <Button
                              className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-5 py-2 text-sm font-body font-medium transition-all duration-300"
                              data-testid={`customise-btn-${product.id}`}
                            >
                              Customise
                              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Link>
                    );
                  })}
                </div>
              )}

              {!loading && products.length === 0 && (
                <div className="text-center py-20" data-testid="no-products">
                  <p className="text-gray-500 text-base font-body">No products found in this category.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
